import { useContext, useEffect, useMemo, useState } from "react";
import AuthContext from "../../../AppContext/AuthContext";
import ProfileContext from "../../../AppContext/ProfileContext";
import { canAccessMediaLibrary } from "../../../services/permission";
import { getSpeak2Profile } from "../../Speak2ContentAPIs/Speak2Profile";

/** Merges Speak2 `/users/profile/get` with Prntyr profile for Media Library permissions. */
export function useMediaLibraryProfile(overrideProfile) {
  const { IsSpeak2User } = useContext(AuthContext);
  const profileContext = useContext(ProfileContext);
  const baseProfile =
    overrideProfile ?? profileContext?.UserProfileData ?? null;

  const [speak2Content, setSpeak2Content] = useState(null);
  const [isProfileLoading, setIsProfileLoading] = useState(Boolean(IsSpeak2User));

  useEffect(() => {
    if (!IsSpeak2User) {
      setSpeak2Content(null);
      setIsProfileLoading(false);
      return;
    }

    let cancelled = false;
    setIsProfileLoading(true);

    (async () => {
      try {
        const res = await getSpeak2Profile();
        if (cancelled) return;
        const content = res?.data?.Success === true ? res.data.Content : null;
        setSpeak2Content(content);
      } catch {
        if (cancelled) return;
        setSpeak2Content(null);
      } finally {
        if (!cancelled) {
          setIsProfileLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [IsSpeak2User]);

  const profile = useMemo(() => {
    if (!speak2Content) return baseProfile;
    return {
      ...baseProfile,
      ...speak2Content,
      Roles: speak2Content.Roles ?? baseProfile?.Roles,
    };
  }, [baseProfile, speak2Content]);

  return { profile, isProfileLoading };
}

/** Power User and above (Speak2 users with staff-management roles). */
export function useCanAccessMediaLibraryAdmin() {
  const { IsSpeak2User } = useContext(AuthContext);
  const { profile } = useMediaLibraryProfile();
  const canAccess = IsSpeak2User === true && canAccessMediaLibrary(profile);

  return { canAccess };
}
